import D3Throttle from '@utilities/throttle';

export type StickyHeaderPluginOptions = {
  stickyHeight?: number;
  version?: number;
  handlersDelay?: number;
  stickyThreshold?: number;
  navigationClass?: string;
  selectActiveItem?: boolean;
  stickyHeader?: boolean;
  forceUseStickyThreshold?: boolean;
};

const defaults: StickyHeaderPluginOptions = {
  version: 1,
  handlersDelay: 100,
  stickyThreshold: 100,
  navigationClass: '.navigation',
  selectActiveItem: false,
  stickyHeader: true,
  forceUseStickyThreshold: false,
};

export default class StickyHeaderPlugin {
  opt: StickyHeaderPluginOptions;
  dom: HTMLElement;
  wrapper: HTMLElement;
  navigation: HTMLElement;
  activeItem: HTMLElement;
  navigationMenu: HTMLElement;

  constructor(dom: HTMLElement, o: StickyHeaderPluginOptions = {}) {
    this.opt = {
      ...defaults,
      ...o,
    };
    this.dom = dom;
  }

  init() {
    // Cache dom elements
    this.wrapper = this.dom.querySelector('.js-sticky-wrap');
    this.navigation = this.opt.navigationClass ? document.querySelector(this.opt.navigationClass) : null;

    if (this.opt.stickyHeader) {
      // Init event handlers
      const throttleScroll = new D3Throttle(
        window,
        this.toggleSticky,
        this.opt.handlersDelay,
        ['load.stickyheader', 'scroll.stickyheader'],
        this,
      );
      throttleScroll.run();
    }

    if (this.opt.selectActiveItem) {
      this.setScrollToActive();
    }
  }

  setScrollToActive() {
    this.activeItem = this.wrapper?.querySelector('.navigation__item.active');
    this.navigationMenu = this.wrapper?.querySelector('.navigation__menu');
    if (this.activeItem) {
      const scroll = this.activeItem.scrollLeft - 10;
      this.navigationMenu.scrollTo({ left: scroll, behavior: 'smooth' });
    }
  }

  getOtherStickyHeights() {
    // Check all sticky elements and get their height
    const stickyNav = [
      ...(this.navigation?.querySelectorAll('.affix') ?? []),
      ...(document.querySelectorAll('.navigation--sticky') ?? []),
    ].filter((elem) => elem !== this.wrapper);

    return stickyNav.reduce((acc, elem) => acc + elem.clientHeight, 0);
  }

  toggleSticky() {
    if (!this.wrapper) return;
    const totalHeight = this.getOtherStickyHeights();
    this.dom.style.setProperty('top', `${totalHeight}px`);

    // Calculate header height
    if (!this.wrapper.classList.contains('affix')) {
      this.opt.stickyHeight = this.dom.clientHeight;
      if (!this.opt.forceUseStickyThreshold) {
        this.opt.stickyThreshold = this.dom.offsetTop + this.opt.stickyHeight - totalHeight;
      }
    }

    // Calculate scroll position
    const sp = window.scrollY;

    // Toggle sticky bar
    if (sp >= this.opt.stickyThreshold) {
      if (!this.wrapper.classList.contains('affix')) {
        this.dom.style.setProperty('height', `${this.opt.stickyHeight}px`);
        this.wrapper.classList.add('affix');

        if (totalHeight) {
          this.dom.style.setProperty('top', `${totalHeight}px`);
        } else {
          this.dom.style.removeProperty('top');
        }
      }
    } else if (sp < this.opt.stickyThreshold) {
      if (this.wrapper.classList.contains('affix')) {
        this.wrapper.classList.remove('affix');
        this.dom.style.removeProperty('top');
        this.dom.style.removeProperty('height');
      }
    }
  }
}
